import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { IJob, JobPartCategory } from "../../models/Job";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import Card from "../shared/Card/Card";
import ContactFromId from "../contacts/ContactFromId";
import usePartAssembly from "../../data/usePartAssembly";
import VehiclePartSummary from "./VehiclePartSummary";
import VehicleAssemblyProgress from "./VehicleAssemblyProgress";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import openInNewTab from "../../utils/openInNewTab";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useState } from "react";
import useJobMutations from "../../data/useJobMutations";
import { useQueryClient } from "@tanstack/react-query";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { hasJobStarted, isJobPartActioned } from "../../utils/partCompletion";

interface JobOverviewCardProps {
  job?: IJob;
  jobPartCategories: JobPartCategory[];
  showView?: boolean;
}

export default function JobOverviewCard({ job, jobPartCategories, showView = false }: JobOverviewCardProps) {
  const { t } = useTranslation("jobs");
  const queryClient = useQueryClient();

  const { assemblies } = usePartAssembly();
  const { remove } = useJobMutations();
  const { deleteHandlers } = useDefaultCRUDHandlers("Job");
  const [deleteJob, setDeleteJob] = useState<IJob | null | undefined>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const handleDeleteJob = () => {
    setDeleteLoading(true);
    if (deleteJob) {
      remove.mutate(deleteJob.id, {
        onSuccess: () => {
          queryClient.invalidateQueries(["vehicles"]);
          setDeleteOpen(false);
          deleteHandlers.onSuccess();
        },
        onError: deleteHandlers.onError,
        onSettled: () => {
          setDeleteLoading(false);
        },
      });
    } else {
      deleteHandlers.onError();
      setDeleteLoading(false);
    }
  };

  const jobStatus = job?.completedOn || job?.dismantlingEnd
    ? "Completed"
    : hasJobStarted(job)
      ? "In Progress"
      : "Not Started";

  const allAssemblies = assemblies.data?.data ?? [];
  const assemblyCategoryIds = new Set(
    allAssemblies.flatMap((assembly) =>
      (assembly.partAssemblyPartCategories ?? [])
        .map((category) => category.partCategoryId)
        .filter((categoryId): categoryId is string => !!categoryId)
    )
  );

  const assemblyProgress = allAssemblies
    .map((assembly) => {
      const categoryIdsForAssembly = new Set(
        (assembly.partAssemblyPartCategories ?? [])
          .map((category) => category.partCategoryId)
          .filter((categoryId): categoryId is string => !!categoryId)
      );

      const assemblyParts = jobPartCategories.filter(
        (jobPartCategory) =>
          !!jobPartCategory.partCategoryId && categoryIdsForAssembly.has(jobPartCategory.partCategoryId)
      );

      const completedCount = assemblyParts.filter(isJobPartActioned).length;
      const totalCount = assemblyParts.length;

      return {
        id: assembly.id,
        name: assembly.name,
        order: assembly.order ?? Number.MAX_SAFE_INTEGER,
        completedCount,
        totalCount,
      };
    })
    .filter((assembly) => assembly.totalCount > 0);

  const nonAssemblyParts = jobPartCategories.filter((jobPartCategory) => {
    if (!jobPartCategory.partCategoryId) {
      return true;
    }

    return !assemblyCategoryIds.has(jobPartCategory.partCategoryId);
  });
  const nonAssemblyProgress = nonAssemblyParts.length > 0
    ? {
      id: "non-assembly",
      name: t("Non-Assembly"),
      completedCount: nonAssemblyParts.filter(isJobPartActioned).length,
      totalCount: nonAssemblyParts.length,
    }
    : null;

  const progressColumns = nonAssemblyProgress ? [...assemblyProgress, nonAssemblyProgress] : assemblyProgress;

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title={t("Delete job?")}
        message={t("Are you sure you want to remove this job?\n It will no longer appear on the job calendar.")}
        confirmButtonText={t("Delete")}
        onConfirm={handleDeleteJob}
        isLoading={deleteLoading}
      />

      <Card bodyClassName="p-4">
        <div className="">

          <div className="space-y-4 px-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* replace is for old title that had duration baked in */}
                  <h1 className="text-xl font-medium">{job?.title.replace(/-[^-]*$/, "")}</h1>
                  {job?.lead?.vehicle?.vrm && <h1 className="text-lg">- {job?.lead?.vehicle?.vrm}</h1>}
                  <span
                    className={classNames(
                      "rounded-full px-2.5 py-1 text-xs font-semibold",
                      jobStatus === "Completed" && "bg-green-100 text-green-700",
                      jobStatus === "In Progress" && "bg-blue-100 text-blue-700",
                      jobStatus === "Not Started" && "bg-gray-100 text-gray-600",
                    )}
                  >
                    {t(jobStatus)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6 xl:flex-row">
              <div className="min-w-0 flex-1">
                <VehicleAssemblyProgress progressColumns={progressColumns} />
              </div>

              <div className="flex justify-end xl:min-w-[340px] xl:max-w-[380px] xl:flex-1">
                <div className="w-full xl:min-w-[340px]">
                  <VehiclePartSummary jobPartCategories={jobPartCategories} />
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <div className="flex flex-wrap items-start gap-x-12 gap-y-3">
                <div className="min-w-[160px]">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Assigned To")}</p>
                  <p className="text-sm font-medium text-gray-800">{job?.mechanicId ? <ContactFromId contactId={job.mechanicId} /> : t("Not Allocated")}</p>
                </div>
                <div className="min-w-[160px]">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Created")}</p>
                  <p className="text-sm font-medium text-gray-800">
                    {job?.created ? formatDateTime(job.created, DateTime.DATE_MED) : "-"}
                  </p>
                </div>
                <div className="min-w-[160px]">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Scheduled")}</p>
                  <p className="text-sm font-medium text-gray-800">
                    {job?.startDate ? formatDateTime(job.startDate, DateTime.DATETIME_SHORT) : "-"}
                  </p>
                </div>
                <div className="min-w-[160px]">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Completed")}</p>
                  <p className="text-sm font-medium text-gray-800">
                    {job?.completedOn
                      ? formatDateTime(job.completedOn, DateTime.DATETIME_SHORT)
                      : job?.dismantlingEnd
                        ? formatDateTime(job.dismantlingEnd, DateTime.DATETIME_SHORT)
                        : "-"}
                  </p>
                </div>
              </div>

              <div className="flex gap-x-2 items-center">
                {showView && (
                  <ButtonInput
                    label={t("View")}
                    isSubmit={false}
                    onClick={() => openInNewTab(`/jobs/${job?.id}`)}
                    classes={""}
                  />
                )}

                <ButtonInput
                  label={t("Delete Job")}
                  isSubmit={false}
                  onClick={() => {
                    setDeleteOpen(true);
                    setDeleteJob(job);
                  }}
                  classes={"!bg-red-500 hover:!bg-red-700"}
                />
              </div>
            </div>

          </div>


        </div>
      </Card>
    </>
  );
}
